import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogIn, LogOut, Menu, Moon, Plane, Sun, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { MenuAirBengue } from "@/components/MenuAirBengue";
import { getUser, clearUser } from "@/lib/user";
import { AIR_BENGUE } from "@/lib/airBengue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ordre demandé par le propriétaire : LaGo, Love, ClubVIP, AirBengue, Abonnement,
// Annonces, Déposer, Messages, Contacts. AirBengue garde son entrée animée (`air: true`).
const links = [
  { to: "/", label: "Accueil", testid: "nav-link-home" },
  { to: "/categorie/lago", label: "LaGo", testid: "nav-link-lago" },
  { to: "/love", label: "Love", testid: "nav-link-love" },
  { to: "/categorie/vip", label: "ClubVIP", testid: "nav-link-vip" },
  { to: AIR_BENGUE.route, label: AIR_BENGUE.court, testid: "nav-link-air-bengue", air: true },
  { to: "/abonnement", label: "Abonnement", testid: "nav-link-subscription" },
  // Retour terrain : « Annonces » consulte les annonces (le dépôt est une action distincte)
  { to: "/annonces-consulter", label: "Annonces", testid: "nav-link-annonces" },
  { to: "/annonces", label: "Déposer", testid: "nav-link-deposer" },
  { to: "/messages", label: "Messages", testid: "nav-link-messages" },
  { to: "/contacts", label: "Contacts", testid: "nav-link-contacts" },
];

export const Navbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const [sombre, setSombre] = useState(() => document.documentElement.classList.contains("dark"));
  const [menuOuvert, setMenuOuvert] = useState(false);

  // Changement de page : le tiroir mobile se referme.
  useEffect(() => { setMenuOuvert(false); }, [pathname]);

  // Battement de cœur : alimente la pastille « en ligne » vue par les autres membres.
  useEffect(() => {
    if (!user?.id) return undefined;
    const signaler = () => fetch(`${API}/profiles/${user.id}/presence`, { method: "POST" }).catch(() => {});
    signaler();
    const minuterie = setInterval(signaler, 120000);
    return () => clearInterval(minuterie);
  }, [user?.id]);

  const basculerTheme = () => {
    const actif = document.documentElement.classList.toggle("dark");
    localStorage.setItem("fodindin_theme", actif ? "dark" : "light");
    setSombre(actif);
  };

  const deconnexion = () => {
    clearUser();
    toast.success("Vous êtes déconnecté(e). À bientôt !");
    navigate("/");
    window.location.reload();
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-[#12100E]/85 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo dark />
        </Link>
        <div className="hidden lg:flex items-center gap-0.5">
          {links.map((l) =>
            l.air ? (
              <MenuAirBengue key={l.to} />
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  pathname === l.to
                    ? "text-[#D4AF37] bg-white/[0.06]"
                    : "text-stone-300 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                {l.label}
              </Link>
            ),
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuOuvert((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Ouvrir le menu"
            aria-expanded={menuOuvert}
            className="lg:hidden p-2.5 rounded-full text-stone-200 hover:text-white hover:bg-white/[0.08] transition-colors duration-200"
          >
            {menuOuvert ? <X size={18} /> : <Menu size={18} />}
          </button>
          <button
            onClick={basculerTheme}
            data-testid="nav-theme-toggle"
            title={sombre ? "Mode clair" : "Mode sombre"}
            className="p-2.5 rounded-full text-stone-300 hover:text-white hover:bg-white/[0.08] transition-colors duration-200"
          >
            {sombre ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-link" className="hidden sm:inline text-sm font-semibold text-[#D4AF37] hover:text-white transition-colors">
                  Admin
                </Link>
              )}
              <Link to="/compte" data-testid="nav-user-firstname" className="hidden sm:inline text-sm text-stone-300 hover:text-white transition-colors">
                {user.firstname}
              </Link>
              <button
                onClick={deconnexion}
                data-testid="nav-logout-btn"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
              >
                <LogOut size={14} /> Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link
                to="/connexion"
                data-testid="nav-login-btn"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
              >
                <LogIn size={14} /> Connexion
              </Link>
              <Link
                to="/inscription"
                data-testid="nav-register-btn"
                className="hidden sm:inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#C84428] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
              >
                Créer mon profil
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Navigation mobile : tous les onglets accessibles, AirBengue inclus. */}
      {menuOuvert && (
        <div className="lg:hidden max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-white/[0.06] bg-[#12100E]/98 px-4 pb-6 pt-3 backdrop-blur-xl"
          data-testid="nav-mobile-menu">
          <div className="grid grid-cols-2 gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setMenuOuvert(false)}
                className={`flex items-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold transition-colors duration-200 ${
                  pathname === l.to ? "text-[#D4AF37] bg-white/[0.08]" : "text-stone-200 bg-white/[0.04] hover:bg-white/[0.09]"
                }`}
                style={l.air ? { background: AIR_BENGUE.colorSoft, color: "#A7BBFF" } : undefined}
              >
                {l.air && <Plane size={14} className="shrink-0 -rotate-45" />}
                {l.label}
                {l.air && (
                  <span className="ml-auto rounded-full bg-[#FFD43B] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#12100E]">
                    New
                  </span>
                )}
              </Link>
            ))}
          </div>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link-mobile" onClick={() => setMenuOuvert(false)}
              className="mt-2 block rounded-2xl bg-white/[0.04] px-4 py-3.5 text-sm font-semibold text-[#D4AF37]">
              Bastion admin
            </Link>
          )}
          {user ? (
            <Link to="/compte" data-testid="nav-compte-mobile" onClick={() => setMenuOuvert(false)}
              className="mt-2 block rounded-2xl bg-white/[0.04] px-4 py-3.5 text-sm font-semibold text-stone-200">
              Mon compte
            </Link>
          ) : (
            <Link to="/inscription" data-testid="nav-register-btn-mobile" onClick={() => setMenuOuvert(false)}
              className="mt-2 block rounded-2xl bg-[#E05638] px-4 py-3.5 text-center text-sm font-bold text-white">
              Créer mon profil
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};
